import { yupResolver } from '@hookform/resolvers/yup';
import {
    FieldErrors,
    FieldValues,
    Path,
    Resolver,
    useForm,
    UseFormProps,
    UseFormReturn,
} from 'react-hook-form';
import * as yup from 'yup';

/**
 * When a form speaks up.
 *
 * The first error lands on blur or on picking a value -- never while a field is still
 * being typed into for the first time, which is what would make a half-typed address read
 * as a broken one -- and from then on the field corrects itself live as it is fixed.
 *
 * Exported for the rare form that cannot go through {@link useValidatedForm}. Prefer the
 * hook: one call site is what stops the two settings drifting apart.
 */
export const FORM_VALIDATION_MODE = {
    mode: 'onTouched',
    reValidateMode: 'onChange',
} as const;

/**
 * Every form in the app. One trigger, one resolver, one place either can be changed.
 *
 * <p>Reading `formState.isValid` subscribes to whole-form validity and makes react-hook-form
 * run the resolver once on mount. That is supported in `onTouched` mode and is *not* in the
 * default `onSubmit` one -- which is why a submit button that disables itself and this
 * trigger have to ship together.
 *
 * <p>The `Resolver<T>` cast is the reason this is a wrapper rather than a spreadable const:
 * `yupResolver` infers the schema's own inferred type, which matches the form's value type
 * at every call site and cannot be *proved* to at one generic one. Paid once here rather
 * than as friction in each form.
 */
export const useValidatedForm = <T extends FieldValues>(
    schema: yup.ObjectSchema<any>,
    options?: Omit<UseFormProps<T>, 'mode' | 'reValidateMode' | 'resolver'>
): UseFormReturn<T> =>
    useForm<T>({
        ...options,
        ...FORM_VALIDATION_MODE,
        resolver: yupResolver(schema) as unknown as Resolver<T>,
    });

/**
 * The two props every field wires by hand, taken from the one place the message lives.
 *
 * <p>Spread onto a `TextField` beside `register`, it removes the whole class of bug where
 * `error` names one field and `helperText` another.
 *
 * <p>Flat names only. Every form in this app has a flat shape except the working-hours
 * week, which reaches its rows through `errors.days?.[index]` directly.
 *
 * @param hint the helper text when there is nothing wrong. Passing one keeps the row's
 *             height constant, so an appearing message never nudges the layout.
 */
export const fieldProps = <T extends FieldValues>(
    errors: FieldErrors<T>,
    name: Path<T>,
    hint?: string
): { error: boolean; helperText?: string } => {
    const message: string | undefined =
        (errors as Record<string, { message?: string } | undefined>)[name]?.message;

    return { error: !!message, helperText: message ?? hint };
};
